/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 관심표명자 목록 수정 컴포넌트
 * 버전 : 1.0
 * 파일명 : InterestedModify.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-18, 강동형 : 저작권 추가, 레이아웃 변경
 * 수정 : 2025-12-29, 강동형 : 학과 UI 추가
 *=================================================================
 */

import React, { useEffect, useState } from 'react'
import { styled, Box, Typography, TextField, IconButton } from '@mui/material'; 
import { promoResultType, schoolType } from 'src/types/PromoType';
import InputBoxOnChange from '../inputbox/InputBoxOnChange';
import DatePickerInterestedAddRecentDate from './DatePickerInterestedAddRecentDate';
import { usePromoAddInterest } from 'src/stores/PromoStore';
import DatePickerInterestedAddRecentDateBirthDate from './DatePickerInterestedAddRecentDateBirthDate';
import ButtonResultModifyInterest from './ButtonResultModifyInterest';
import { useNavbarStore } from 'src/stores/UIStore';
import Icon_Trash from 'src/assets/images/Icon_Trash.svg'
import { deleteInterested } from 'src/api/Interested';
import InterestedSelectType from './InterestedSelectType';
import { Icon_ArrowBack } from '../common/CommonIcons';
import { NextButton, NextButtonWrapper } from '../common/CommonStyled';
import { useModalStore } from 'src/stores/ModalStore';
import { ModalContentDelete, ModalFooterDelete, ModalTitleDelete } from './ModalFieldsInterested';
import { toast } from 'react-toastify';


const Container = styled(Box)`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  overflow-y: auto;
  padding-top: 0px;
`;

const Label = styled(Typography)({
  color: '#7F7F89',
  fontSize: '14px',
  paddingBottom: '10px'
})

const LargeTextField = styled(TextField)`
  height: '100%';
`;

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center', // ✅ camelCase로 수정
  fontSize:'18px',
  fontWeight:'bold',
  padding:'20px'
});

const GenderWrapper = styled('div')({
  display : 'flex',
  alignItems: 'baseline',
  justifyContent :'center',
  border : '1px solid #ccc',
  padding: '3px',
  borderRadius:'5px',
  minHeight:'46px',
  maxHeight:'46px'
})

const GenderContainer = styled('div')<{ isSelected: boolean }>(({ isSelected }) => ({
  display : 'flex',
  alignItems: 'center',
  justifyContent :'center',
  height:'100%',
  width:'100%',
  borderRadius:'4px',
  fontSize:'14px',
  backgroundColor: isSelected ? '#06799B' : 'none',
  color : isSelected ? 'white' : 'black'
}))

interface Props{
    currentPromotion: promoResultType | undefined
    setSelected:React.SetStateAction<any>
    selected:any
}

const InterestedModify = ({currentPromotion, setSelected, selected}:Props) => {
    const isHighSchool = currentPromotion?.school?.school_class === '고등학교' ? true:false
    const schoolData = currentPromotion?.school
    const setField = usePromoAddInterest((state)=>state.setField)
    const school_class = usePromoAddInterest((state)=>state.school_class)
    const [name,setName] = useState(selected.name)
    const [phone, setPhone] = useState(selected.phone)
    const [gender,setGender] = useState(selected.gender ==='남자' ? true:false)
    const [memo, setMemo] = useState(selected.memo)
    const [major, setMajor] = useState(selected?.major ?? '')
    const setNavField = useNavbarStore((state)=>state.setField)
    const modalSetField = useModalStore((state)=>state.setField)
    const setAcceptAction = useModalStore((state)=>state.setAcceptAction)
    
    useEffect(() => {    
      if (!schoolData) return;
      if (isHighSchool) {
        setField('school_class', 'high')
      } else if (currentPromotion?.school?.school_class === '대학교'){
        setField('school_class', 'univ')
      } else{
        setField('school_class', 'etc')
      }
      
      setField('type',selected.status)
    }, [schoolData, isHighSchool]);
  
    useEffect(()=>{
      setField('promotion_id',currentPromotion?.result_id)
    },[])

    useEffect(()=>{
      if(gender){
        setField('gender','남자')
      }
      else{
        setField('gender','여자')
      }
    },[gender])

    useEffect(()=>{
        setNavField('isvisible',false)
        setField('recent_date',selected?.counsel_date)
        setField('birth_date',selected?.birthday)
        return()=>{
            setNavField('isvisible',true)
        }
    },[])

    useEffect(()=>{
      setField('name',name)
    },[name])
    useEffect(()=>{
      setField('phone',phone)
    },[phone])
    useEffect(()=>{
      setField('memo',memo)
    },[memo])
    useEffect(()=>{
      setField('major',major)
    },[major])

    const handleName = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setName(e.target.value)
    }
    const handlePhone = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setPhone(e.target.value)
    }
    const handleMemo = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setMemo(e.target.value)
    }
    const handleMajor = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setMajor(e.target.value)
    }

  const handleDelete = async ()=>{
    const response = await deleteInterested(selected.id,school_class)
    setSelected('')
    if (response.status === 'OK') modalSetField('open',false)
    else{
      modalSetField('open',false)
      toast.error('삭제실패',{autoClose:2000})
    }
  }

  const onClickDeleteAccount = ()=>{
    modalSetField('type','CENTER')
    modalSetField('padding','24px')
    modalSetField('width','calc(100vw - 40px)')
    modalSetField('title',ModalTitleDelete)
    modalSetField('content',ModalContentDelete)
    modalSetField('footer',<ModalFooterDelete/>)
    modalSetField('open',true)
    setAcceptAction(handleDelete)
  }

  return (
    <>
      <Header>
        <IconButton onClick={() => setSelected('')} sx={{position:'absolute', left:'10px'}}>
          <Icon_ArrowBack/>
        </IconButton>
        관심 표명자 수정
        <IconButton onClick={onClickDeleteAccount} sx={{position:'absolute',right:'20px'}}>
          <img src={Icon_Trash} alt="" />
        </IconButton>
      </Header>
      <Container>
      <Label>*학교명</Label>
        <Box display="flex" alignItems="baseline" justifyContent="flex-end">
        <InputBoxOnChange 
          value={
            schoolData
              ? (schoolData as schoolType).school_name
              : ''
          }
          disabled={true}
        />
        </Box>
      <Label marginTop='10px'>*최근상담일자</Label>
        <DatePickerInterestedAddRecentDate/>
      <Label marginTop='10px'>*이름</Label>
        <Box display="flex" alignItems="baseline" justifyContent="flex-end" >
            <InputBoxOnChange onChange={handleName} value={name}/>
        </Box>
      <Label marginTop='10px'>*연락처</Label>
        <Box display="flex" alignItems="baseline" justifyContent="flex-end">
            <InputBoxOnChange onChange={handlePhone} value={phone}/>
        </Box>
      <Label marginTop='10px'>*생년월일</Label>
        <DatePickerInterestedAddRecentDateBirthDate/>
      <Label marginTop='10px'>*학과</Label>
        <Box display="flex" alignItems="baseline" justifyContent="flex-end">
            <InputBoxOnChange onChange={handleMajor} value={major}/>
        </Box>
      <Label marginTop='10px'>*성별</Label>
        <GenderWrapper >
            <GenderContainer isSelected={gender} onClick={()=>setGender(true)}>
              남자
            </GenderContainer>
            <GenderContainer isSelected={!gender} onClick={()=>setGender(false)}>
              여자
            </GenderContainer>
        </GenderWrapper>
      <Label marginTop='10px'>*상태</Label>
      <Box display="flex" alignItems="baseline" justifyContent="flex-end">
          <InterestedSelectType/>
      </Box>
      <Box flex={1} display='flex'flexDirection={'column'}>
      <Label>메모</Label>
      <LargeTextField fullWidth onChange={handleMemo} multiline rows={4} placeholder="메모를 입력하세요" value={memo}/>
      </Box>
    </Container>
    <NextButtonWrapper $isBorder={true}  style={{gap:'10px'}}>
      <NextButton style={{background:'#F5F5F5', color:'#7F7F89', minWidth:'90px', width:'90px', boxShadow:'none'}} onClick={()=>setSelected('')}>취소</NextButton>
      <ButtonResultModifyInterest setSelected={setSelected} selected={selected}/>
    </NextButtonWrapper>
    </>
  )
}

export default InterestedModify